import { Wand2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const TIPO_LABELS = {
  it_activa:           'IT activa',
  ausencias_regulares: 'Ausencias',
  cambio_salario:      'Cambio salario',
  irpf_divergente:     'IRPF divergente',
  primer_mes:          'Primer mes',
  baja_parcial:        'Baja parcial',
};

/**
 * AutoResolvePreview — preview card for AUTO_RESOLVE_PAYROLL.
 * Shows tier breakdown + sample employees + button to confirm.
 */
export default function AutoResolvePreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <Wand2 className="w-3.5 h-3.5" />
        <span>Auto-resolución aprobada — aplicando overrides…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Auto-resolución cancelada</span>
      </div>
    );
  }

  const tipoEntries = Object.entries(preview?.tipo_breakdown ?? {}).filter(([, n]) => n > 0);
  const sample = preview?.sample_empleados ?? [];

  return (
    <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/20 p-4 space-y-3 my-2 max-w-sm">
      <div>
        <Badge variant="outline" className="text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 text-[10px] mb-1">
          Requiere aprobación
        </Badge>
        <p className="text-sm font-semibold text-foreground leading-tight">
          Auto-resolución · {preview?.periodo}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {preview?.empleados_afectados ?? 0} empleado{(preview?.empleados_afectados ?? 0) !== 1 ? 's' : ''} con issues auto-aplicables
        </p>
      </div>

      {tipoEntries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tipoEntries.map(([tipo, n]) => (
            <span key={tipo} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
              {TIPO_LABELS[tipo] ?? tipo} · {n}
            </span>
          ))}
        </div>
      )}

      {sample.length > 0 && (
        <div className="text-[11px] text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto border-t border-violet-200 dark:border-violet-800 pt-2">
          {sample.map((emp, i) => (
            <p key={i} className="truncate">
              <span className="font-medium">{emp.nombre}</span>
              {emp.tipos && emp.tipos.length > 0 && (
                <span className="text-muted-foreground"> · {emp.tipos.map(t => TIPO_LABELS[t] ?? t).join(', ')}</span>
              )}
            </p>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove} className="flex-1 h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white">
          <Wand2 className="w-3 h-3 mr-1" />Resolver
        </Button>
      </div>
    </div>
  );
}
